import { LUKITA_URL } from '@/config'
import { ErrorFactory } from './error-factory'
import { fileDownloadResponse, getHeaders, streamedResponse } from './utils'

interface Aborter {
  abortController: () => AbortController | null
  onAbortError?: () => void
}

type Body = Record<string, unknown>
interface ExecuteOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: Record<string, unknown>
  queryParams?: Record<string, string | number | boolean>
  filters?: unknown
  isStream?: boolean
  isFile?: boolean
  onStreamMessage?: (message: string) => void
  onProgress?: (progress: number) => void
  aborter?: Aborter
}

export const execute = async (
  route: string,
  {
    method = 'GET',
    body,
    queryParams = {},
    filters = null,
    isStream = false,
    isFile = false,
    onStreamMessage,
    onProgress,
    aborter,
  }: ExecuteOptions = {},
): Promise<Response | string | [Blob, string | null] | void> => {
  const query = new URLSearchParams()
  if (filters) {
    query.set('filters', JSON.stringify(filters))
  }
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      query.set(key, value as string)
    })
  }

  const signal = aborter?.abortController()?.signal
  return fetch(`${LUKITA_URL}${route}?${query.toString()}`, {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers: await getHeaders({ 'Content-Type': 'application/json' }),
    signal,
  })
    .then(async (response) => {
      if (response.status === 204) return response
      if (!response.body) {
        return ErrorFactory('The response body is empty.', response.status, {})
      }

      if (!response.ok) {
        const error = await response.json()
        return ErrorFactory(error.message, response.status, body, error.code)
      }

      if (isFile && onProgress) {
        return fileDownloadResponse(response, { onProgress })
      }

      if (isStream && onStreamMessage) {
        return streamedResponse(response, {
          abortController: aborter?.abortController,
          onStreamMessage,
        })
      }

      return response
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        aborter?.onAbortError?.()
        return
      }

      throw error
    })
}

export const executePost = (
  route: string,
  body?: Body,
  options: Omit<ExecuteOptions, 'method' | 'body'> = {},
): Promise<Response> =>
  execute(route, { method: 'POST', body, ...options }) as Promise<Response>

export const executeGet = (
  route: string,
  options: Omit<ExecuteOptions, 'method' | 'body'> = {},
): Promise<Response> =>
  execute(route, { method: 'GET', ...options }) as Promise<Response>

export const executePut = (
  route: string,
  body?: Body,
  options: Omit<ExecuteOptions, 'method' | 'body'> = {},
): Promise<Response> =>
  execute(route, { method: 'PUT', body, ...options }) as Promise<Response>

export const executePatch = (
  route: string,
  body?: Body,
  options: Omit<ExecuteOptions, 'method' | 'body'> = {},
): Promise<Response> =>
  execute(route, { method: 'PATCH', body, ...options }) as Promise<Response>

export const executeDelete = (
  route: string,
  body?: Body,
  options: Omit<ExecuteOptions, 'method' | 'body'> = {},
): Promise<Response> =>
  execute(route, { method: 'DELETE', body, ...options }) as Promise<Response>
